import { z } from 'zod';
import { STRINGS } from '../strings';

const checkThresholdSchema = z.object({
  /** Max pages to crawl beyond homepage. */
  maxExtraPages: z.number().int().min(0).max(50).default(10),
  /** Fetch timeout in ms. */
  fetchTimeoutMs: z.number().int().min(1000).max(60000).default(15000),
  /** Max response body bytes. */
  maxBodyBytes: z.number().int().min(100_000).max(20_000_000).default(5_000_000),
  /** Politeness delay between fetches (ms). */
  politenessDelayMs: z.number().int().min(0).max(5000).default(1000),
  /** Re-run limit per site per day. */
  rerunPerSitePerDay: z.number().int().min(1).max(10).default(1),
  /** Result cache hours. */
  resultCacheHours: z.number().int().min(1).max(168).default(24),
  /** A5: minimum words on a service page. */
  servicePageMinWords: z.number().int().min(50).max(1000).default(150),
  /** E2 meta description bounds. */
  metaDescriptionMin: z.number().int().min(20).max(200).default(70),
  metaDescriptionMax: z.number().int().min(50).max(320).default(165),
  /** E1 title bounds. */
  titleMin: z.number().int().min(10).max(80).default(25),
  titleMax: z.number().int().min(30).max(120).default(65),
  /** B5 max required form fields. */
  formRequiredMax: z.number().int().min(1).max(50).default(8),
  /** E7 max missing-alt ratio (0–1). */
  altMissingRatioMax: z.number().min(0).max(1).default(0.4),
  /** D2 PSI thresholds. */
  psiLcpMaxSeconds: z.number().min(1).max(30).default(4),
  psiPerfMinScore: z.number().int().min(0).max(100).default(50),
  /** E8 max listed broken links. */
  maxBrokenLinksListed: z.number().int().min(1).max(50).default(10),
});

const tradePackSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  /** Placeholder service names for templates. */
  defaultServices: z.array(z.string()).default([]),
});

const productConfigSchema = z.object({
  checks: checkThresholdSchema.default({}),
  tradePacks: z.array(tradePackSchema).min(1),
  copy: z.object({
    productName: z.string().min(1),
    tagline: z.string().min(1),
  }),
  uploads: z
    .object({
      maxImageBytes: z.number().int().positive().default(8 * 1024 * 1024),
      maxImagesPerInquiry: z.number().int().positive().default(5),
      signedUrlTtlSeconds: z.number().int().positive().default(15 * 60),
      allowedMime: z.array(z.string()).default(['image/jpeg', 'image/png', 'image/webp', 'image/gif']),
    })
    .default({}),
});

export type ProductConfig = z.infer<typeof productConfigSchema>;
export type CheckThresholds = ProductConfig['checks'];

const DEFAULT_TRADE_PACKS: z.infer<typeof tradePackSchema>[] = [
  { id: 'plumbing', label: 'Plumbing', defaultServices: ['Drain cleaning', 'Water heater', 'Leak repair'] },
  { id: 'hvac', label: 'HVAC', defaultServices: ['AC repair', 'Furnace tune-up', 'Duct cleaning'] },
  { id: 'electrical', label: 'Electrical', defaultServices: ['Panel upgrade', 'Outlet install', 'Lighting'] },
  { id: 'roofing', label: 'Roofing', defaultServices: ['Roof repair', 'Inspection', 'Replacement'] },
  { id: 'landscaping', label: 'Landscaping', defaultServices: ['Lawn care', 'Hardscape', 'Cleanup'] },
  { id: 'painting', label: 'Painting', defaultServices: ['Interior', 'Exterior', 'Cabinet'] },
  { id: 'remodeling', label: 'Remodeling', defaultServices: ['Kitchen', 'Bath', 'Addition'] },
  { id: 'general', label: 'General', defaultServices: ['Service call', 'Estimate', 'Repair'] },
];

const RAW_DEFAULT = {
  checks: {},
  tradePacks: DEFAULT_TRADE_PACKS,
  copy: {
    productName: STRINGS.product.name,
    tagline: STRINGS.product.tagline,
  },
  uploads: {},
};

let cached: ProductConfig | null = null;

/** Validate and cache product config (thresholds, trade packs, copy). Call at import/boot. */
export function loadProductConfig(overrides?: unknown): ProductConfig {
  const merged =
    overrides && typeof overrides === 'object'
      ? {
          ...RAW_DEFAULT,
          ...(overrides as Record<string, unknown>),
          checks: {
            ...RAW_DEFAULT.checks,
            ...((overrides as { checks?: object }).checks ?? {}),
          },
          copy: {
            ...RAW_DEFAULT.copy,
            ...((overrides as { copy?: object }).copy ?? {}),
          },
          uploads: {
            ...RAW_DEFAULT.uploads,
            ...((overrides as { uploads?: object }).uploads ?? {}),
          },
        }
      : RAW_DEFAULT;

  const parsed = productConfigSchema.safeParse(merged);
  if (!parsed.success) {
    throw new Error(`Invalid product config: ${parsed.error.message}`);
  }
  cached = parsed.data;
  return cached;
}

/** Returns cached config; loads defaults on first access. */
export function getProductConfig(): ProductConfig {
  if (!cached) return loadProductConfig();
  return cached;
}

/** Reset cache (tests only). */
export function resetProductConfigCache(): void {
  cached = null;
}

// Validate defaults at module import (boot-time guard).
loadProductConfig();
